/**
 * Path capture for the Report a problem feature (AKL-798).
 *
 * Deliberately its own module with **no imports at all** — no React, no libraries, no
 * storage, no network. Two callers need it: the dialog (`ReportProblemModal`) and the axios
 * response interceptor (`src/api/axios.tsx`), and that interceptor is in the import graph of
 * every file under `src/api/`. Putting this beside the browser/environment helpers in
 * `reportContext.ts` would drag `react-device-detect` (which requires `react` and builds a
 * `UAParser` at module load) into the interceptor purely to strip a query string.
 *
 * Privacy, not convenience: review and project URLs carry identifiers, and a captured path
 * ends up in an email and a Slack message.
 */

/**
 * The path of a URL or path, with any query string and fragment removed.
 *
 * String-based on purpose. The `URL` constructor cannot be used here because the two callers
 * pass different shapes: the dialog passes an absolute pathname (`window.location.pathname`)
 * while the interceptor passes axios's **relative** `error.config.url` (e.g.
 * `/users/me?x=1`), and `new URL("/users/me?x=1")` throws. Truncating at the first `?` or `#`
 * handles both, and an absolute URL reduces to its path because the scheme's `//` survives
 * untouched — so `capturePath` is applied to a pathname, never to a full href, when the
 * caller has a choice.
 * @param pathOrUrl a pathname, a relative request url, or an absolute URL; may be undefined
 * @returns the path with no query string and no hash, or "" when there was nothing to read
 */
export function capturePath(pathOrUrl: string | undefined | null): string {
  if (!pathOrUrl) return "";
  const cutAt = pathOrUrl.search(/[?#]/);
  const withoutQuery = cutAt === -1 ? pathOrUrl : pathOrUrl.slice(0, cutAt);
  // An absolute URL: keep only its path so the host never travels in the `page` field.
  const schemeEnd = withoutQuery.indexOf("://");
  if (schemeEnd !== -1) {
    const afterHost = withoutQuery.indexOf("/", schemeEnd + 3);
    return afterHost === -1 ? "/" : withoutQuery.slice(afterHost);
  }
  return withoutQuery;
}
